<template>
    <div class="editor_scene">
        <template v-if="loading">
            <Loading/>
        </template>
        <div class="editor_scene-title">
            <h1>{{ $t('editorTemplatesTitle') }}</h1>
        </div>
        <div class="template_formats">
            <div
                v-for="(template, index) in templateFormats"
                :data-id="template.id"
                :key="index"
                class="template_formats-item"
                @click="(e) => {addTemplateData(e, template)}"
            >
                <div class="template_formats-item-box" :style="colors">
                    <button
                        class="delete_btn btn"
                        @click.stop="checkIfFormatProjectsEmpty(template)"
                    >
                        {{ $t('editorDelete') }}
                    </button>
                    <button
                        class="edit_btn btn"
                        @click.stop="editFormat(template)"
                    >
                        {{ $t('editorEdit') }}
                    </button>
                    <div class="template_formats-item-box-visual">
                        <div :style="formatPreview(template.pageWidth, template.pageHeight)"></div>
                    </div>
                    <div class="template_formats-item-box-name">
                        {{ template.pageWidth }} x {{ template.pageHeight }} mm
                    </div>
                </div>
                <div class="template_formats-item-title">
                    {{ template.name }}
                </div>
            </div>
            <div class="template_formats-item">
                <button
                    @click="showFormModal = true"
                    class="add_new-btn btn"
                >
                    <inline-svg
                        :src="add_icon"
                        width="45px"
                        height="45px"
                    />
                </button>
                <div class="template_formats-item-title">
                    {{ $t('editorTemplatesCreate') }}
                </div>
            </div>
        </div>
        <Modal
            v-if="showEditModal"
            @close="showEditModal = false"
        >
            <template v-slot:header>
                {{ $t('editorTemplatesEditName', { formatName: `${ formatToEditName }` }) }}
            </template>
            <template v-slot:body>
                <form method="post" action="?" @submit.prevent="submitFormatNameChange" class="modalForm">
                    <div class="form-group">
                        <label for="editedFormatInput">{{ $t('editorTemplatesSetNewName') }}</label>
                        <input
                            id="editedFormatInput"
                            v-model="editedFormatInput"
                            type="text"
                            name="editedFormatInput"
                        />
                    </div>
                    <div class="form-footer">
                        <button @click="showEditModal = false" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            type="submit"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorSubmit') }}</button>
                    </div>
                </form>
            </template>
        </Modal>
        <Modal 
            v-if="showFormModal"
            @close="showFormModal = false"
        >
            <template v-slot:header>
                <strong>{{ $t('editorTemplatesCreateNew') }}</strong>
            </template>
            <template v-slot:body>
                <form method="post" action="?" @submit.prevent="submit" class="modalForm">
                    <div class="form-group">
                        <label for="formatName">{{ $t('editorTemplatesName') }}</label>
                        <input
                            id="formatName"
                            v-model="formatName"
                            type="text"
                            name="formatName"
                        />
                    </div>
                    <div class="form-group">
                        <label for="formatWidth">{{ $t('editorTemplatesWidth') }}</label>
                        <input
                            id="formatWidth"
                            v-model.number="formatWidth"
                            type="number"
                            name="formatWidth"
                        />
                    </div>
                    <div class="form-group">
                        <label for="formatHeight">{{ $t('editorTemplatesHeight') }}</label>
                        <input
                            id="formatHeight"
                            v-model.number="formatHeight"
                            type="number"
                            name="formatHeight"
                        />
                    </div>
                    <div class="form-group">
                        <label for="formatBleed">{{ $t('editorTemplatesBleed') }}</label>
                        <input
                            id="formatBleed"
                            v-model.number="formatBleed"
                            type="number"
                            name="formatBleed"
                        />
                    </div>
                    <div class="form-footer">
                        <button @click="showFormModal = false" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            type="submit"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorSubmit') }}</button>
                    </div>
                </form>
            </template>
        </Modal>
        <Modal 
            v-if="showDeleteModal"
            @close="showDeleteModal = false"
        >
            <template v-slot:header>
                <strong>{{ $t('editorTemplateWarning') }}</strong>
            </template>
            <template v-slot:body>
                <div class="modalForm">
                    <div class="form-footer">
                        <button @click="showDeleteModal = false" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            @click="deleteFormat(formatToRemove)"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorTemplateRemove') }}</button>
                    </div>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { RepositoryFactory } from '../../repositories/repositoryFactory';
    const FormatRepository = RepositoryFactory.get('formats');

    import Modal from '@/components/Global/Modal.vue';
    import Loading from '@/components/Global/Loading.vue';
    import add_icon from '@/assets/icons/add.svg';

    export default {
        data() {
            return {
                templateFormats: [],
                showFormModal: false,
                formatName: null,
                formatWidth: null,
                formatHeight: null,
                formatBleed: null,
                loading: true,
                add_icon,
                showDeleteModal: false,
                formatToRemove: null,
                showEditModal: false,
                formatToEditName: null,
                formatToEditId: null,
                editedFormatInput: null
            }
        },
        methods: {
            addTemplateData(e, template) {
                const data = {
                    id: template.id,
                    name: template.name,
                    pageWidth: template.pageWidth,
                    pageHeight: template.pageHeight,
                    pageBleed: template.pageBleed
                }
                this.$store.dispatch('ADD_TEMPLATE_DATA', data).then(() => {
                    this.$router.replace({ name: 'templateProjects', params: { templateName: template.name } })
                });              
            },
            submit() {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }
                const formatData = {
                    name: this.formatName,
                    pageWidth: this.formatWidth,
                    pageHeight: this.formatHeight,
                    pageBleed: this.formatBleed
                }

                FormatRepository.createFormat(JSON.stringify(formatData), headers)
                .then(response => {
                    this.showFormModal = false;
                    this.loading = true;
                    this.templateFormats = [
                        ...this.templateFormats,
                        {
                            id: response.data.data.id,
                            name: response.data.data.name,
                            pageWidth: response.data.data.pageWidth,
                            pageHeight: response.data.data.pageHeight,
                            pageBleed: response.data.data.pageBleed,
                            projects: response.data.data.projects
                        }
                    ]
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.formatName = null;
                    this.formatWidth = null;
                    this.formatHeight = null;
                    this.formatBleed = null;
                });
            },
            checkIfFormatProjectsEmpty(template) {
                template.projects.length > 0 ? (this.showDeleteModal = true, this.formatToRemove = template.id) : this.deleteFormat(template.id);
            },
            deleteFormat(formatId) {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }
                FormatRepository.deleteFormat(formatId, headers)
                .then(response => {
                    this.templateFormats = this.templateFormats.filter(format => format.id !== formatId)
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.showDeleteModal = false;
                })
            },
            editFormat(template) {
                this.showEditModal = true;
                this.formatToEditName = template.name;
                this.formatToEditId = template.id;
                this.editedFormatInput = template.name;
            },
            submitFormatNameChange() {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }
                FormatRepository.updateFormat({ name: this.editedFormatInput, id: this.formatToEditId }, headers)
                    .then(response => {
                        let editedFormatObject = this.templateFormats.find(template => template.id === this.formatToEditId);
                        editedFormatObject.name = this.editedFormatInput;
                    })
                    .catch(error => {
                        console.log(error)
                    })
                    .finally(() => {
                        this.showEditModal = false;
                        this.formatToEditName = null;
                        this.formatToEditId = null;
                        this.editedFormatInput = null;
                    });
            },
            formatPreview(width, height) {
                const ratio = width / height;
                let widthNew, heightNew;

                if (ratio > 1) {
                    widthNew = 50;
                    heightNew = 50 / ratio;
                } else if (ratio < 1) {
                    heightNew = 50;
                    widthNew = 50 * ratio;
                } else {
                    heightNew = 50;
                    widthNew = 50;
                }

                return `width: ${widthNew}px; height: ${heightNew}px; background-color: white;`;
            }
        },
        computed: {
            ...mapGetters([
                'getSessionDetails'
            ]),
            colors() {
                return {
                    '--primary-color': `#${this.getSessionDetails.editorSetup.colors.primaryColor}`
                }
            }
        },
        created() {
            const webPageSize = {
                px: {
                    width: null,
                    height: null
                },
                mm: {
                    width: null,
                    height: null
                },
                hasValue: false,
                ratio: null
            }
            this.$store.dispatch('SAVE_PAGE_WEB_SIZE', webPageSize); 
            const headers = {
                'api-key' : 'session',
                'api-sign' : this.getSessionDetails.token
            }
            FormatRepository.get(headers, 150)
            .then((result) => {
                const entities = result.data.data.entities
                entities.forEach(entity => {
                    this.templateFormats = [
                        ...this.templateFormats,
                        entity
                    ]
                })
            })
            .finally(() => {
                this.loading = false;
            })
        },
        components: {
            Modal,
            Loading
        }
    }
</script>

<style lang="scss" scoped>
    @import '@/styles/setup/_variables.scss';

    .editor_scene {
        padding: 15px;
        width: 100%;
    }
    .template_formats-item-box{
        position: relative;
        background-color: var(--primary-color);
    }
    .template_formats-item-box-visual {
        width: 70px;
        height: 70px;
        position: absolute;
        left: 15px;
        top: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .template_formats-item-box-name {
        position: absolute;
        bottom: 5px;
        width: 100%;
    }
    .template_formats-item .add_new-btn {
        align-items: center;
    }
    .edit_btn {
        width: auto;
        height: auto;
        position: absolute;
        background: #fff;
        padding: 3px 5px;
        left: 0px;
        top: 0px;
        color: #310048;
        border: 0;
        z-index: 1;
        border-radius: 0;
        display: none;
    }
    .edit_btn:hover {
        background: #ddd;
    }
    .delete_btn {
        width: auto;
        height: auto;
        position: absolute;
        background: #E91E63;
        padding: 3px 5px;
        right: 0px;
        top: 0px;
        color: #fff;
        border: 0;
        z-index: 1;
        border-radius: 0;
        display: none;
    }
    .delete_btn:hover {
        background: #CA1150;
    }
    .template_formats-item-box:hover .delete_btn,
    .template_formats-item-box:hover .edit_btn {
        display: block;
    }
</style>